/**
 * Authentication utilities using Clerk + registro flow para VIP.
 *
 * El flujo VIP requiere:
 *   1. user.unsafeMetadata.{alias, telegram, birthDate}  (CompleteRegistration)
 *   2. user.publicMetadata.approved === true             (admin approve)
 *
 * Helpers portados del fan site original.
 */

import type { UserResource } from "@clerk/types";
import { useUser } from "@clerk/clerk-react";
import { readMembershipProfileFromMeta } from "../../shared/membershipClerkMetadata";

export type UserRole = "admin" | "user";

export const MIN_REGISTRATION_AGE = 18;

export interface RegistrationData {
  alias: string;
  birthDate: string; // ISO o YYYY-MM-DD
  telegram: string;
}

// ─── Role / admin checks ──────────────────────────────────────────────────────

/** True si publicMetadata otorga permisos de panel/API admin (alineado con api/lib/clerkAuth). */
export function isPublicMetadataAdmin(
  meta: Record<string, unknown> | null | undefined,
): boolean {
  if (!meta || typeof meta !== "object") return false;
  const role = meta.role;
  if (role === "admin" || role === "super_admin") return true;
  return meta.super_admin === true || meta.is_super_admin === true;
}

/** Lee role desde Clerk publicMetadata */
export function useUserRole(): UserRole {
  const { user } = useUser();
  const meta = user?.publicMetadata as Record<string, unknown> | undefined;
  return isPublicMetadataAdmin(meta) ? "admin" : "user";
}

export function useIsAdmin(): boolean {
  return useUserRole() === "admin";
}

/** True si publicMetadata tiene role "super_admin" o super_admin/is_super_admin flags */
export function isPublicMetadataSuperAdmin(
  meta: Record<string, unknown> | null | undefined,
): boolean {
  if (!meta || typeof meta !== "object") return false;
  if (meta.role === "super_admin") return true;
  if ((meta.role === "admin") && (meta.super_admin === true || meta.is_super_admin === true)) return true;
  return false;
}

export function useIsSuperAdmin(): boolean {
  const { user } = useUser();
  const meta = user?.publicMetadata as Record<string, unknown> | undefined;
  return isPublicMetadataSuperAdmin(meta);
}

/** Quality Assurance: role="quality_assurance" | "qa" o flag quality_assurance/qa.
 *  Super admins también califican para sim tools.
 */
export function isPublicMetadataQualityAssurance(
  meta: Record<string, unknown> | null | undefined,
): boolean {
  if (!meta || typeof meta !== "object") return false;
  const role = meta.role;
  if (role === "quality_assurance" || role === "qa") return true;
  if (meta.quality_assurance === true || meta.qa === true) return true;
  // super admins can act as QA
  return isPublicMetadataSuperAdmin(meta);
}

export function useIsQualityAssurance(): boolean {
  const { user } = useUser();
  const meta = user?.publicMetadata as Record<string, unknown> | undefined;
  return isPublicMetadataQualityAssurance(meta);
}

export function isClerkAdmin(user: UserResource | null | undefined): boolean {
  if (!user) return false;
  return isPublicMetadataAdmin(user.publicMetadata as Record<string, unknown>);
}

/**
 * "Revenue-only admin" — el rol acotado `{ role: "admin", approved: true }`
 * SIN flags de super admin ni QA. Este rol solo puede ver el dashboard de
 * métricas de ganancias (Revenue, brand-isolated por BRAND_SOURCE_KEY); el
 * resto del panel es super-admin. Un super admin nunca es revenue-only.
 */
export function isPublicMetadataRevenueOnlyAdmin(
  meta: Record<string, unknown> | null | undefined,
): boolean {
  if (!isPublicMetadataAdmin(meta)) return false;
  if (isPublicMetadataSuperAdmin(meta)) return false;
  if (isPublicMetadataQualityAssurance(meta)) return false;
  return true;
}

export function useIsRevenueOnlyAdmin(): boolean {
  const { user } = useUser();
  const meta = user?.publicMetadata as Record<string, unknown> | undefined;
  return isPublicMetadataRevenueOnlyAdmin(meta);
}

// ─── Profile completion (registro VIP) ────────────────────────────────────────

/**
 * Returns true cuando el user tiene los campos del CompleteRegistration:
 * alias, birthDate, telegram en unsafeMetadata.
 *
 * Usado por:
 *   - CompleteRegistration: para no pedir el form si ya está completo
 *   - PendingApproval: para no quedarse loop si vuelve sin completar
 */
export function isProfileComplete(user: UserResource | null | undefined): boolean {
  if (!user) return false;
  // Admins skip — siempre considerados "completos"
  if (isClerkAdmin(user)) return true;
  const { alias, birthDate, telegram } = readMembershipProfileFromMeta(
    user.unsafeMetadata as Record<string, unknown>,
  );
  return alias.length >= 3 && birthDate.length >= 4 && telegram.length >= 3;
}

export function getRegistrationData(
  user: UserResource | null | undefined,
): RegistrationData {
  return readMembershipProfileFromMeta(
    user?.unsafeMetadata as Record<string, unknown>,
  );
}

// ─── Validation ───────────────────────────────────────────────────────────────

export function isAdultBirthDate(dateIsoOrYmd: string): boolean {
  if (!dateIsoOrYmd) return false;
  const parsed = new Date(
    dateIsoOrYmd.length === 10 ? `${dateIsoOrYmd}T00:00:00.000Z` : dateIsoOrYmd,
  );
  if (Number.isNaN(parsed.getTime())) return false;
  const now = new Date();
  let age = now.getFullYear() - parsed.getFullYear();
  const m = now.getMonth() - parsed.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < parsed.getDate())) age -= 1;
  return age >= MIN_REGISTRATION_AGE;
}

export function validateRegistrationData(input: RegistrationData): string | null {
  const alias = (input.alias ?? "").trim();
  const telegram = (input.telegram ?? "").trim();
  const birthDate = (input.birthDate ?? "").trim();
  if (alias.length < 3 || alias.length > 30) {
    return "El alias debe tener entre 3 y 30 caracteres.";
  }
  if (telegram.length < 3 || telegram.length > 50) {
    return "El usuario de Telegram debe tener entre 3 y 50 caracteres.";
  }
  if (!birthDate) {
    return "Ingresá tu fecha de nacimiento.";
  }
  if (!isAdultBirthDate(birthDate)) {
    return `Debes tener al menos ${MIN_REGISTRATION_AGE} años para continuar.`;
  }
  return null;
}

// ─── Approved check ───────────────────────────────────────────────────────────

/**
 * Aprobación admin para acceso a contenido privado (Telegram link).
 *
 * Comportamiento:
 *   - Admins → siempre aprobados.
 *   - publicMetadata.approved === true → aprobado.
 *   - publicMetadata.approved === false → NO aprobado (explícito).
 *   - Sin flag → NO aprobado (default seguro para nuevo flujo VIP).
 *
 * Nota: este cambio respecto a antes (que default era true) es intencional —
 * el flujo VIP requiere approval explícito del admin para asignar Telegram link.
 */
export function isUserApproved(user: UserResource | null | undefined): boolean {
  if (!user) return false;
  if (isClerkAdmin(user)) return true;
  return user.publicMetadata?.approved === true;
}

// ─── Display helpers ──────────────────────────────────────────────────────────

export function getUserDisplayName(
  user: UserResource | null | undefined,
): string {
  if (!user) return "";
  const { alias } = readMembershipProfileFromMeta(
    user.unsafeMetadata as Record<string, unknown>,
  );
  if (alias) return alias;
  if (user.firstName) return user.firstName;
  return user.primaryEmailAddress?.emailAddress?.split("@")[0] ?? "";
}
